'use client';

import { useState, useEffect } from 'react';
import { createClient } from '@/lib/supabase/client';
import Icon from '@/components/ui/AppIcon';

interface IndexData {
  symbol: string;
  name: string;
  value: number | null;
  change: number | null;
  changePct: number | null;
  high: number | null;
  low: number | null;
  volume: string | null;
  aiConfidence: number | null;
  hasData: boolean;
}

const INDEX_SYMBOLS = ['SPX', 'NDX', 'DJI', 'RUT', 'VIX', 'DXY'];
const INDEX_NAMES: Record<string, string> = {
  SPX: 'S&P 500', NDX: 'NASDAQ 100', DJI: 'Dow Jones',
  RUT: 'Russell 2000', VIX: 'CBOE VIX', DXY: 'US Dollar Index',
};

const EMPTY_INDICES: IndexData[] = INDEX_SYMBOLS.map(symbol => ({
  symbol, name: INDEX_NAMES[symbol], value: null, change: null,
  changePct: null, high: null, low: null, volume: null, aiConfidence: null, hasData: false,
}));

export default function KeyIndicesPanel() {
  const [indices, setIndices] = useState<IndexData[]>(EMPTY_INDICES);
  const [loading, setLoading] = useState(true);
  const [flashMap, setFlashMap] = useState<Record<string, 'up' | 'down'>>({});
  const [noApiKey, setNoApiKey] = useState(false);
  const supabase = createClient();

  const fetchIndices = async () => {
    try {
      const res = await fetch('/api/market/indices');
      const json = await res.json();

      if (json.error === 'Unauthorized') return;

      if (json.indices && json.indices.length > 0) {
        const hasAnyData = json.indices.some((i: any) => i.hasData);
        setNoApiKey(!hasAnyData && !json.hasData);

        setIndices(prev => {
          const next: IndexData[] = json.indices.map((idx: any) => ({
            symbol: idx.symbol,
            name: idx.name || INDEX_NAMES[idx.symbol] || idx.symbol,
            value: idx.value,
            change: idx.change,
            changePct: idx.changePct,
            high: idx.high,
            low: idx.low,
            volume: idx.volume ? `${(idx.volume / 1e9).toFixed(1)}B` : null,
            aiConfidence: null,
            hasData: idx.hasData,
          }));

          // Flash detection
          const flashes: Record<string, 'up' | 'down'> = {};
          next.forEach((n, i) => {
            const old = prev[i];
            if (old && n.value != null && old.value != null && n.value !== old.value) {
              flashes[n.symbol] = n.value > old.value ? 'up' : 'down';
            }
          });
          if (Object.keys(flashes).length > 0) {
            setFlashMap(flashes);
            setTimeout(() => setFlashMap({}), 600);
          }

          return next;
        });
      }
    } catch (err) {
      // Keep empty state on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndices();

    // Real-time subscription for market_prices changes
    const channel = supabase
      .channel('market_prices_indices')
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'market_prices',
      }, () => {
        fetchIndices();
      })
      .subscribe();

    // Polling fallback every 30s
    const pollId = setInterval(fetchIndices, 30000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(pollId);
    };
  }, []);

  return (
    <div className="bg-[#1E293B] border border-[#334155] rounded-lg overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <Icon name="ChartBarIcon" size={16} variant="solid" className="text-sky-500" />
          <span className="text-sm font-semibold text-slate-200 font-mono">Key Indices</span>
        </div>
        <div className="flex items-center gap-1.5">
          {loading ? (
            <div className="w-3 h-3 border border-sky-500 border-t-transparent rounded-full animate-spin" />
          ) : (
            <div className={`w-1.5 h-1.5 rounded-full ${indices.some(i => i.hasData) ? 'bg-emerald-500 animate-pulse' : 'bg-slate-600'}`} />
          )}
          <span className="text-xs text-slate-500 font-mono">
            {loading ? 'FETCHING...' : indices.some(i => i.hasData) ? 'LIVE' : 'NO DATA'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 divide-x divide-y divide-[#334155]">
        {indices.map(idx => {
          const flash = flashMap[idx.symbol];
          const isPos = (idx.changePct ?? 0) >= 0;
          return (
            <div
              key={idx.symbol}
              className={`p-3 transition-all duration-500 cursor-pointer hover:bg-[#334155]/50 ${
                flash === 'up' ? 'bg-emerald-500/10' : flash === 'down' ? 'bg-red-500/10' : ''
              }`}
            >
              <div className="flex items-start justify-between mb-1">
                <div>
                  <span className="text-xs font-mono font-bold text-sky-400">{idx.symbol}</span>
                  <p className="text-xs text-slate-500 truncate">{idx.name}</p>
                </div>
                {idx.hasData && (
                  <Icon
                    name={isPos ? 'ArrowTrendingUpIcon' : 'ArrowTrendingDownIcon'}
                    size={12}
                    variant="outline"
                    className={isPos ? 'text-emerald-400' : 'text-red-400'}
                  />
                )}
              </div>

              {idx.hasData && idx.value != null ? (
                <>
                  <p
                    className={`text-base font-mono font-bold transition-colors duration-300 ${
                      flash === 'up' ? 'text-emerald-400' : flash === 'down' ? 'text-red-400' : 'text-slate-100'
                    }`}
                  >
                    {idx.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  <div className={`flex items-center gap-1 mt-0.5 ${isPos ? 'text-emerald-400' : 'text-red-400'}`}>
                    <span className="text-xs font-mono">
                      {isPos ? '+' : ''}{(idx.changePct ?? 0).toFixed(2)}%
                    </span>
                    <span className="text-xs text-slate-500 font-mono">
                      ({isPos ? '+' : ''}{(idx.change ?? 0).toFixed(2)})
                    </span>
                  </div>
                </>
              ) : (
                <div className="mt-1">
                  <p className="text-base font-mono font-bold text-slate-600">—</p>
                  <p className="text-xs font-mono text-slate-600 mt-0.5">No data</p>
                </div>
              )}

              <div className="mt-1.5 flex items-center gap-1">
                <span className="text-xs text-slate-600 font-mono">AI</span>
                <div className="flex-1 h-1 bg-[#334155] rounded-full overflow-hidden">
                  {idx.aiConfidence != null ? (
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        idx.aiConfidence >= 70 ? 'bg-sky-500' : idx.aiConfidence >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${idx.aiConfidence}%` }}
                    />
                  ) : (
                    <div className="h-full w-0 bg-slate-600" />
                  )}
                </div>
                <span className="text-xs font-mono text-slate-500">
                  {idx.aiConfidence != null ? `${idx.aiConfidence}%` : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {!loading && !indices.some(i => i.hasData) && (
        <div className="px-4 py-2 border-t border-[#334155] bg-amber-500/5">
          <p className="text-xs text-amber-400 font-mono">
            ⚠ Market data unavailable — configure an API key in{' '}
            <a href="/user-settings" className="underline hover:text-amber-300">Settings → API Configuration</a>
            {' '}(Polygon.io, Twelve Data, or Alpha Vantage)
          </p>
        </div>
      )}
    </div>
  );
}